/**
 * Domain nouns the business uses, defined once and read by the structural
 * tables — those are keyed by an enum member, a field key or a route, so the
 * same noun would otherwise be written out once per table.
 *
 * A noun belongs here only when more than one structural table needs it.
 * Words any app would have — Speichern, Abbrechen — are `COMMON`.
 *
 * No sentences, no articles, no adjectives: surfaces build labels out of
 * these, and those words inflect for the noun beside them. `Gesamtes Objekt`
 * is a literal in the cost table, not `Gesamtes ${TERMS.property}`.
 */
export const TERMS = {
  /** The state of something not yet in force: a project, a contract. */
  draft: 'Entwurf',
  /** The product. Read by the logo's alt text and the rail's wordmark. */
  appName: 'BrickUp',
  /** Where only a mark fits — the collapsed rail. */
  appNameShort: 'BU',
  /** The buyer. A role, a field on a project, and a side of the cost table. */
  customer: 'Kunde',
  /** A Gewerk, a GU or a planner the project is built with: the other side
   *  of the money. */
  subcontractor: 'Nachunternehmer',
  /** The house or flat under renovation. Never "Immobilie" — that is marketing. */
  property: 'Objekt',
  propertyPlural: 'Objekte',
  /** A room used for storage, and the amenity of having one. */
  storageRoom: 'Abstellraum',
  /** What the customer picked in the configurator. */
  selection: 'Auswahl',
  /** Where a record came from — a listing portal, a side of the cost table. */
  source: 'Quelle',
  /** One picture of the gallery a reader pages through. The single picture an
   *  entry is drawn by is its Kartenbild, which is a different slot. */
  previewImage: 'Vorschaubild',
  previewImagePlural: 'Vorschaubilder',
  /** One place a catalogue entry, an image or an element type is referenced. */
  usage: 'Verwendung',
  usagePlural: 'Verwendungen',
  /** Referenced nowhere — a column value, a filter and a badge all say it. */
  unused: 'Unbenutzt',
  email: 'E-Mail',
  /** The advisor a customer reaches — a nav entry, a setting and its row. */
  contactPerson: 'Ansprechpartner',
  /** Who answers for a project and sells it. The role, the post a project
   *  names them for, and the column of the project list. */
  advisor: 'Berater',
  /** Who runs a project. */
  projectManager: 'Projektleiter',
  /** Who runs the work on site. */
  siteManager: 'Bauleiter',
  configurator: 'Konfigurator',
  logs: 'Logs',
  /** The customer's verdict on a matched property. */
  feedback: 'Feedback',
  /** The staff-assigned identifier of a room, e.g. R-03. */
  roomCode: 'Raum-ID',
  /** The work BrickUp sells. */
  renovation: 'Sanierung',
  /** One piece of renovation work — the catalogue entry the configurator
   *  offers and internal planning adds as a position. */
  measure: 'Maßnahme',
  measurePlural: 'Maßnahmen',
  /** What the offer states a measure as: the wording that says what is owed.
   *  Said by the measure's form, by a position's pane and by the switch that
   *  shows the texts on the Auswertung. */
  serviceDescription: 'Leistungsbeschreibung',
  serviceDescriptionPlural: 'Leistungsbeschreibungen',
  /** The sentence or two a measure is drawn by — on its card, and under a
   *  position that came from it. */
  shortDescription: 'Kurzbeschreibung',
  /** A product a measure is carried out with. */
  product: 'Produkt',
  productPlural: 'Produkte',
  /** One node of the configurator tree — it carries questions, may hold
   *  further nodes, and a node of the storey or room kind sets the scope
   *  everything under it is asked at. Also a named set of element types. */
  group: 'Gruppe',
  groupPlural: 'Gruppen',
  /** One decision the configurator puts to the customer — the unit both the
   *  catalogue tree and a resolved pick are read by. */
  question: 'Frage',
  questionPlural: 'Fragen',
  /** One question as the customer walks past it. */
  step: 'Schritt',
  /** A named expression in the formula library. */
  formula: 'Formel',
  formulaPlural: 'Formeln',
  /** The branch they live in: material for any field that takes code, not a
   *  price source of its own. */
  formulaCollection: 'Formelsammlung',
  /** How a measure arrives at what it charges: the stages, their blocks and
   *  what each one comes to. A tab of the measure's pane, a price mode, and a
   *  permission. */
  calculation: 'Kalkulation',
  /** One step of a calculation. The steps after it may read its subtotal. */
  stage: 'Stufe',
  stagePlural: 'Stufen',
  /** One term of a price with no catalogue entry of its own: an amount, a
   *  percentage, an expression. A branch of the catalogue, and a row of a
   *  stage. */
  block: 'Preisbaustein',
  blockPlural: 'Preisbausteine',
  /** An ordered bundle of the things that make a price — Preisbausteine,
   *  Produkte, Arbeitsleistungen — inserted as one. Never `group`, which is
   *  the configurator's. */
  priceGroup: 'Preisgruppe',
  priceGroupPlural: 'Preisgruppen',

  /** One thing a Preisgruppe charges — a Preisbaustein, a product, an
   *  Arbeitsleistung. What the bundle is made of; a Preisgruppe is not a club
   *  and the things in it are not members of one. */
  member: 'Bestandteil',
  memberPlural: 'Bestandteile',
  /** Work the company charges at a rate — a branch of the catalogue, and one
   *  kind of term a price is written in. */
  labourItem: 'Arbeitsleistung',
  labourItemPlural: 'Arbeitsleistungen',
  /** A storey counted towards the property's floor count. */
  fullFloor: 'Vollgeschoss',
  /** One space inside the property — a field on it, and a formula scope. */
  room: 'Raum',
  /** The storey a room stands on — a field on it, and a formula scope. */
  floor: 'Geschoss',
  /** The building as a whole — the fields that describe it, and the block
   *  of positions that belong to no single room. */
  building: 'Gebäude',
  /** A quantity or option an admin defines on the property, which the
   *  configurator prices against. */
  elementTypePlural: 'Element-Typen',
  /** The four axes a product is filed under. Each is a field on the product
   *  and a list an admin keeps, so the noun is said by both tables. */
  productCategory: 'Kategorie',
  productCategoryPlural: 'Kategorien',
  productGroup: 'Produktgruppe',
  productGroupPlural: 'Produktgruppen',
  productProperty: 'Produkteigenschaft',
  productPropertyPlural: 'Produkteigenschaften',
  /** What an entry is in its own right, before anything hangs off it — said
   *  by a product's sheet and by an account's pane. */
  masterData: 'Stammdaten',
  /**
   * The bare form of the noun above, on the surfaces that have already said
   * whose — a measure's, a product's, a configurator card's. Not `property`:
   * that key is the Objekt under renovation.
   */
  characteristic: 'Eigenschaft',
  characteristicPlural: 'Eigenschaften',
  designLine: 'Designlinie',
  designLinePlural: 'Designlinien',
  /** The tier the configurator filters by, worn by a measure as well. */
  productStandard: 'Produktstandard',
  productStandardPlural: 'Produktstandards',
  overview: 'Übersicht',
} as const
